import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { describeError } from "../components/States";
import { usePageMeta } from "../lib/seo";

export function NewTicketPage() {
  usePageMeta({
    title: "Open a support ticket",
    description: "Get help from the MarketOnBase team.",
    path: "/tickets/new",
    index: false,
  });

  const navigate = useNavigate();
  const { email } = useAuthPrincipal();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactMethod, setContactMethod] = useState<"EMAIL" | "PHONE">("EMAIL");
  const [contactPhone, setContactPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.createTicket({
        firstName,
        lastName,
        contactMethod,
        contactPhone: contactMethod === "PHONE" ? contactPhone : undefined,
        subject,
        message,
      }),
    onSuccess: (ticket) => navigate(`/tickets/${ticket.id}`, { replace: true }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <div>
        <h1>Open a support ticket</h1>
        <p className="muted">
          For account or platform issues. Something wrong with a specific order?{" "}
          Use that order&apos;s dispute instead — disputes and support tickets are handled
          separately.
        </p>
      </div>

      <form className="card stack" onSubmit={handleSubmit}>
        <div>
          <p className="text-sm muted">Email</p>
          <p>{email ?? "—"}</p>
          <p className="text-sm muted">
            This ticket is always tied to your account email — it can&apos;t be changed here.
          </p>
        </div>

        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="ticket-first-name">First name</label>
            <input
              id="ticket-first-name"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              maxLength={100}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="ticket-last-name">Last name</label>
            <input
              id="ticket-last-name"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              maxLength={100}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="ticket-contact-method">Preferred contact method</label>
          <select
            id="ticket-contact-method"
            value={contactMethod}
            onChange={(e) => setContactMethod(e.target.value as "EMAIL" | "PHONE")}
          >
            <option value="EMAIL">Email (the address above)</option>
            <option value="PHONE">Phone</option>
          </select>
        </div>

        {contactMethod === "PHONE" && (
          <div className="field">
            <label htmlFor="ticket-phone">Phone number</label>
            <input
              id="ticket-phone"
              type="tel"
              autoComplete="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              required
              maxLength={40}
            />
          </div>
        )}

        <div className="field">
          <label htmlFor="ticket-subject">Subject</label>
          <input
            id="ticket-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={200}
          />
        </div>

        <div className="field">
          <label htmlFor="ticket-message">What's going on?</label>
          <textarea
            id="ticket-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            maxLength={5_000}
            rows={6}
          />
        </div>

        {mutation.isError && (
          <p className="field-error-text" role="alert">
            {describeError(mutation.error)}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={
            mutation.isPending ||
            !firstName.trim() ||
            !lastName.trim() ||
            !subject.trim() ||
            !message.trim() ||
            (contactMethod === "PHONE" && !contactPhone.trim())
          }
        >
          {mutation.isPending ? "Submitting…" : "Submit ticket"}
        </button>
      </form>
    </div>
  );
}
